/**
 * DeleteAgentDialog — the "hard delete" confirmation for an AI agent.
 *
 * Two states, picked from the agent's own `isActive`:
 *   - ACTIVE   → a blocking, plain-language explanation ("deactivate it first") with a
 *                one-click route into the deactivate flow. Deleting an active agent could
 *                silently leave WhatsApp / Instagram / web chat with nobody to answer, so
 *                it is refused (the API refuses it too).
 *   - INACTIVE → type-the-name-to-confirm, the same pattern GitHub / Vercel use for
 *                irreversible deletes. The delete button stays disabled until the typed
 *                text matches the agent's name exactly.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutline, PowerSettingsNewOutlined } from '@mui/icons-material';

interface Props {
  open: boolean;
  agentName: string;
  isActive: boolean;
  busy?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Active agent only: hand over to the deactivate flow. */
  onDeactivateInstead: () => void;
}

const IconTile: React.FC<{ tone: 'danger' | 'neutral'; children: React.ReactNode }> = ({ tone, children }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      borderRadius: 2,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: tone === 'danger' ? '#fee2e2' : 'rgba(79, 70, 229, 0.08)',
      color: tone === 'danger' ? '#b91c1c' : '#4f46e5',
    }}
  >
    {children}
  </Box>
);

const DeleteAgentDialog: React.FC<Props> = ({
  open,
  agentName,
  isActive,
  busy = false,
  onClose,
  onConfirm,
  onDeactivateInstead,
}) => {
  const [typed, setTyped] = useState('');

  // A fresh dialog must never inherit the last agent's typed name.
  useEffect(() => {
    if (open) setTyped('');
  }, [open, agentName]);

  const matches = typed.trim() === agentName.trim();

  if (isActive) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <IconTile tone="neutral">
            <PowerSettingsNewOutlined />
          </IconTile>
          Deactivate before deleting
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            <strong>{agentName}</strong> is active, so it may be answering customers right now or be set as
            the default for WhatsApp, Instagram or web chat. Active agents can&apos;t be deleted.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>
            Deactivate it first — you&apos;ll be able to hand its assignments to another agent — then delete
            it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" startIcon={<PowerSettingsNewOutlined />} onClick={onDeactivateInstead}>
            Deactivate agent…
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <IconTile tone="danger">
          <DeleteOutline />
        </IconTile>
        Delete agent
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          This permanently deletes <strong>{agentName}</strong> and its routing settings. Files, text and Q&amp;A
          sources stay in your library and remain available to other agents. Its search index is cleaned up in
          the background. This can&apos;t be undone.
        </Typography>

        <Stack spacing={0.75} sx={{ mt: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Type <strong>{agentName}</strong> to confirm
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={typed}
            disabled={busy}
            placeholder={agentName}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches && !busy) onConfirm();
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!matches || busy}
          onClick={onConfirm}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
        >
          Delete agent
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAgentDialog;
